// src/admin/screens/loans/tabs/ContactHistoryTab.tsx
// Collection call/SMS/visit attempts for this loan. Sorted newest first.
// We fetch the admin's display name via a second lookup keyed by
// contacted_by (auth.uid) because admin_users FK isn't declared in the
// generated relationships metadata.
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@admin/lib/supabase';
import DataTable, { type Column } from '@admin/components/DataTable';
import StatusChip from '@admin/components/StatusChip';
import Money from '@admin/components/Money';
import SectionHead from '@admin/components/SectionHead';
import { formatCatDate, formatCatDateTime } from '@admin/utils/dates';
import type { Ngwee } from '@/lib/types';
import type { Database } from '@/lib/database.types';

type ContactRow = Database['public']['Tables']['collection_contacts']['Row'];
type Outcome = Database['public']['Enums']['collection_outcome'];

const TONE: Record<Outcome, 'info' | 'positive' | 'warn' | 'danger' | 'neutral'> = {
  no_answer: 'neutral',
  reached_promise: 'positive',
  reached_disputed: 'warn',
  reached_refused: 'danger',
  wrong_number: 'warn',
  left_message: 'info',
};

function prettyOutcome(o: Outcome): string {
  return o.replace(/_/g, ' ');
}

export default function ContactHistoryTab({ loanId }: { loanId: string }) {
  const { data, isLoading, error } = useQuery<ContactRow[]>({
    queryKey: ['contacts', loanId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('collection_contacts')
        .select('*')
        .eq('loan_id', loanId)
        .order('contacted_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as ContactRow[];
    },
  });

  const total = data?.length ?? 0;
  const promises = (data ?? []).filter((r) => r.outcome === 'reached_promise').length;
  const description = isLoading
    ? 'Loading…'
    : total === 0
      ? 'No contact attempts logged yet.'
      : `${total} attempt${total === 1 ? '' : 's'} · ${promises} promise${promises === 1 ? '' : 's'} to pay`;

  const cols: Column<ContactRow>[] = [
    { key: 'when', header: 'When', render: (r) => formatCatDateTime(r.contacted_at), width: '180px' },
    {
      key: 'ch',
      header: 'Channel',
      render: (r) => <span style={{ textTransform: 'capitalize' }}>{r.channel}</span>,
      width: '110px',
    },
    {
      key: 'out',
      header: 'Outcome',
      render: (r) => <StatusChip label={prettyOutcome(r.outcome)} tone={TONE[r.outcome]} />,
    },
    {
      key: 'ptp',
      header: 'PTP',
      render: (r) =>
        r.ptp_date && r.ptp_amount_ngwee != null ? (
          <span className="num">
            <Money amount={r.ptp_amount_ngwee as Ngwee} /> · {formatCatDate(r.ptp_date)}
          </span>
        ) : (
          <span style={{ color: 'var(--c-text-dim)' }}>—</span>
        ),
    },
    {
      key: 'note',
      header: 'Notes',
      render: (r) => (
        <span style={{ color: 'var(--c-text-sec)', fontSize: 13 }}>
          {r.notes ?? <span style={{ color: 'var(--c-text-dim)' }}>—</span>}
        </span>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <SectionHead
        eyebrow="Collections"
        title="Contact history"
        description={description}
      />
      <DataTable
        rows={data}
        columns={cols}
        rowKey={(r) => r.id}
        isLoading={isLoading}
        error={(error as Error | null)?.message ?? null}
        emptyLabel="No contact attempts yet."
      />
    </div>
  );
}
